// frontend/src/pages/AdminPage.tsx
import { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { PageLoader, LoadingSpinner } from '../components/LoadingSpinner';
import { dbSelect, dbUpdate } from '../lib/api';
import { useAdminVideos } from '../hooks/useFunVideos';
import {
  Shield,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Folders,
  Star,
  ExternalLink,
  ToggleLeft,
  ToggleRight,
  Film,
  Search,
  Cpu,
  Layers,
  Flag,
  Wrench,
  RefreshCw,
  Key,
  Coins,
} from 'lucide-react';



export function AdminPage() {
  const { user, session, loading: authLoading, creatorFeaturesEnabled, refreshAppSettings } = useAuth();
  const [revenue, setRevenue] = useState<string>('');
  const [payoutStatus, setPayoutStatus] = useState<'idle' | 'processing' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string>('');
  const [stats, setStats] = useState<{
    totalProjects: number;
    totalRuns: number;
    totalUsers: number;
    connectedCreators: number;
  } | null>(null);

  // Project promotion state


  // Creator features toggle state
  const [togglingCreatorFeatures, setTogglingCreatorFeatures] = useState(false);

  // Fun videos — only used for summary counts in the link card
  const { videos: funVideos } = useAdminVideos();

  // Hardcoded admin email check as per requirement
  const ADMIN_EMAIL = 'a.burkov@gmail.com';

  useEffect(() => {
    document.title = 'Admin - Llambada';
    return () => { document.title = 'Llambada'; };
  }, []);

  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) {
      fetchStats();
    }
  }, [user]);

  const fetchStats = async () => {
    if (!session) return;

    try {
      const [projectResult, runResult, userResult, creatorResult] = await Promise.all([
        dbSelect<null>('projects', { token: session.access_token, count: 'exact', head: true }),
        dbSelect<null>('app_usage', { token: session.access_token, count: 'exact', head: true }),
        dbSelect<null>('profiles', { token: session.access_token, count: 'exact', head: true }),
        dbSelect<null>('profiles', {
          token: session.access_token,
          count: 'exact',
          head: true,
          filters: { stripe_account_id: 'not.is.null' }
        }),
      ]);

      setStats({
        totalProjects: projectResult.count || 0,
        totalRuns: runResult.count || 0,
        totalUsers: userResult.count || 0,
        connectedCreators: creatorResult.count || 0,
      });
    } catch (err) {
      console.error('Failed to fetch stats', err);
    }
  };



  const handleToggleCreatorFeatures = async () => {
    if (!session) return;

    setTogglingCreatorFeatures(true);
    try {
      const { error } = await dbUpdate(
        'app_settings',
        { creator_features_enabled: !creatorFeaturesEnabled },
        { id: 'eq.global' },
        { token: session.access_token, returning: false }
      );

      if (error) throw error;

      // Refresh the setting in context so UI updates everywhere
      await refreshAppSettings();
    } catch (err) {
      console.error('Failed to toggle creator features:', err);
    } finally {
      setTogglingCreatorFeatures(false);
    }
  };

  const handlePayout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!revenue || isNaN(Number(revenue))) {
      setMessage('Please enter a valid revenue amount');
      return;
    }
    if (!session) {
      setMessage('Not authenticated');
      return;
    }

    setPayoutStatus('processing');
    setMessage('');

    try {
      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      const response = await fetch(
        `${supabaseUrl}/functions/v1/payout-creators`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            totalRevenue: Number(revenue) * 100 // Convert to cents
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Payout failed');
      }

      setPayoutStatus('success');
      setMessage(result.message || 'Payouts processed successfully');
    } catch (err) {
      setPayoutStatus('error');
      setMessage(err instanceof Error ? err.message : 'An error occurred processing payouts');
    }
  };



  if (authLoading) return <PageLoader />;

  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-theme-primary-muted text-accent-500">
            <Shield className="h-6 w-6" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-theme-text">
              Admin Console
            </h1>
            <p className="text-theme-text-secondary">
              Platform management and revenue distribution
            </p>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="card p-4">
            <div className="text-theme-text-muted text-sm font-medium mb-1">Total Users</div>
            <div className="text-2xl font-bold text-theme-text">{stats?.totalUsers ?? '-'}</div>
          </div>
          <div className="card p-4">
            <div className="text-theme-text-muted text-sm font-medium mb-1">Total Mini-Apps</div>
            <div className="text-2xl font-bold text-theme-text">{stats?.totalProjects ?? '-'}</div>
          </div>
          <div className="card p-4">
            <div className="text-theme-text-muted text-sm font-medium mb-1">Total Runs</div>
            <div className="text-2xl font-bold text-theme-text">{stats?.totalRuns ?? '-'}</div>
          </div>
          <div className="card p-4">
            <div className="text-theme-text-muted text-sm font-medium mb-1">Creators Connected</div>
            <div className="text-2xl font-bold text-theme-text">{stats?.connectedCreators ?? '-'}</div>
          </div>
        </div>

        {/* Project File Inspector — Link to dedicated page */}
        <Link
          to="/admin/files"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-blue-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-blue-500/10 text-blue-500">
            <Search className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-blue-500 transition-colors">Project File Inspector</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              View and download current file versions for any project by URL or UUID.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-blue-500 transition-colors shrink-0" />
        </Link>

        {/* Non-Admin Mini-Apps — Link to dedicated page */}
        <Link
          to="/admin/miniapps"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-emerald-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-emerald-500/10 text-emerald-500">
            <Cpu className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-emerald-500 transition-colors">Non-Admin Mini-Apps</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              Browse all non-admin-built mini-apps with type, CPU/memory settings, public links, and quick inspector access.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-emerald-500 transition-colors shrink-0" />
        </Link>

        {/* Family Fork Generator — Link to dedicated page */}
        <Link
          to="/admin/families"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-blue-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-blue-500/10 text-blue-500">
            <Layers className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-blue-500 transition-colors">Family Fork Generator</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              Select a hidden family project, choose parameter combinations from family.json, and generate/update public forks in bulk.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-blue-500 transition-colors shrink-0" />
        </Link>

        <Link
          to="/admin/base-tool-forks"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-emerald-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-emerald-500/10 text-emerald-500">
            <Wrench className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-emerald-500 transition-colors">Base Tool Fork Generator</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              Draft fork candidates from a verbal base-tool description, build one hidden shared backend, and recreate fork frontends against its saved backend contract.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-emerald-500 transition-colors shrink-0" />
        </Link>

        {/* Code Refresh — Link to dedicated page */}
        <Link
          to="/admin/code-refresh"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-teal-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-teal-500/10 text-teal-500">
            <RefreshCw className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-teal-500 transition-colors">Code Refresh</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              Validate all mini-app code against platform instructions and auto-fix misalignments.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-teal-500 transition-colors shrink-0" />
        </Link>

        <Link
          to="/admin/backend-rebuilds"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-orange-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-orange-500/10 text-orange-400">
            <Wrench className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-orange-400 transition-colors">Backend Rebuilds</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              See every backend-owning mini-app that needs rebuilding after `cloud-run-service` updates, rebuild individual services, or dispatch all rebuilds from the browser.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-orange-400 transition-colors shrink-0" />
        </Link>

        {/* Platform Settings */}
        <div className="card p-6 mb-8">
          <div className="flex items-start gap-4 mb-2">
            <div className="p-3 rounded-lg bg-accent-500/10 text-accent-500">
              <DollarSign className="h-6 w-6" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-theme-text">Platform Settings</h2>
              <p className="text-theme-text-muted text-sm mt-1">
                Configure platform-wide feature flags.
              </p>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between p-4 rounded-lg bg-theme-surface/50 border border-theme-border-muted">
            <div>
              <h3 className="font-medium text-theme-text">Creator Features</h3>
              <p className="text-sm text-theme-text-muted mt-0.5">
                Enable the Earnings page, Stripe Connect for payouts, and the Creator Monetization section in user settings.
              </p>
            </div>
            <button
              onClick={handleToggleCreatorFeatures}
              disabled={togglingCreatorFeatures}
              className="shrink-0 ml-4"
              title={creatorFeaturesEnabled ? 'Disable creator features' : 'Enable creator features'}
            >
              {togglingCreatorFeatures ? (
                <LoadingSpinner size="sm" />
              ) : creatorFeaturesEnabled ? (
                <ToggleRight className="h-8 w-8 text-accent-500" />
              ) : (
                <ToggleLeft className="h-8 w-8 text-theme-text-muted" />
              )}
            </button>
          </div>
        </div>

        {/* Promote Public Mini-Apps — Link to dedicated page */}
        <Link
          to="/admin/promote"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-amber-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-amber-500/10 text-amber-500">
            <Star className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-amber-500 transition-colors">Promote Public Mini-Apps</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              Manage which user-created public mini-apps appear on the home page. Only promoted mini-apps from non-admin users will be visible.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-amber-500 transition-colors shrink-0" />
        </Link>

        {/* Fun Videos Section — Link to dedicated page */}
        <Link
          to="/admin/videos"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-purple-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-purple-500/10 text-purple-500">
            <Film className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-purple-500 transition-colors">Fun Videos</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              Generate and manage AI videos via Grok Imagine. {funVideos.filter(v => v.status === 'ready').length} ready, {funVideos.filter(v => v.status === 'generating').length} generating.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-purple-500 transition-colors shrink-0" />
        </Link>

        {/* Browser Agent — Link to dedicated page */}
        <Link
          to="/admin/agent"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-emerald-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-emerald-500/10 text-emerald-500">
            <Folders className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-emerald-500 transition-colors">Browser Agent</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              Create mini-apps automatically using an AI-driven browser agent. Provide a title and description, and the agent will log in, create the project, and build it.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-emerald-500 transition-colors shrink-0" />
        </Link>

        {/* API Keys — Link to dedicated page */}
        <Link
          to="/admin/api-keys"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-violet-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-violet-500/10 text-violet-500">
            <Key className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-violet-500 transition-colors">API Keys</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              Create and manage API keys for external access to mini-app backends.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-violet-500 transition-colors shrink-0" />
        </Link>

        {/* Paid User Credit Usage — Link to dedicated page */}
        <Link
          to="/admin/credit-usage"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-amber-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-amber-500/10 text-amber-500">
            <Coins className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-amber-500 transition-colors">Paid User Credit Usage</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              See which mini-apps paying users spend their credits on, ranked by total credits consumed.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-amber-500 transition-colors shrink-0" />
        </Link>

        {/* Problem Reports — Link to dedicated page */}
        <Link
          to="/admin/reports"
          className="card p-6 mb-8 flex items-center gap-4 hover:border-red-500/40 transition-colors group"
        >
          <div className="p-3 rounded-lg bg-red-500/10 text-red-500">
            <Flag className="h-6 w-6" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-lg font-semibold text-theme-text group-hover:text-red-500 transition-colors">Problem Reports</h2>
            <p className="text-theme-text-muted text-sm mt-0.5">
              View and dismiss user-submitted problem reports for mini-apps.
            </p>
          </div>
          <ExternalLink className="h-5 w-5 text-theme-text-muted group-hover:text-red-500 transition-colors shrink-0" />
        </Link>

        {/* Payout Section */}
        <div className="card p-6 border-accent-500/30">
          <div className="flex items-start gap-4 mb-6">
            <div className="p-3 rounded-lg bg-theme-success-bg text-theme-success">
              <DollarSign className="h-6 w-6" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-theme-text">Process Monthly Payouts</h2>
              <p className="text-theme-text-muted text-sm mt-1">
                Enter the total subscription revenue collected this month. The system will automatically calculate the creator share (70%) and distribute it based on app usage.
              </p>
            </div>
          </div>

          <form onSubmit={handlePayout} className="space-y-4 max-w-md">
            <div>
              <label htmlFor="revenue" className="block text-sm font-medium text-theme-text-secondary mb-1">
                Total Distributable Revenue (USD)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-text-muted">$</span>
                <input
                  id="revenue"
                  type="number"
                  min="0"
                  step="0.01"
                  value={revenue}
                  onChange={(e) => setRevenue(e.target.value)}
                  className="input pl-8"
                  placeholder="0.00"
                  disabled={payoutStatus === 'processing'}
                />
              </div>
            </div>

            {message && (
              <div className={`flex items-center gap-2 text-sm p-3 rounded-lg ${payoutStatus === 'success' ? 'bg-theme-success-bg text-theme-success' :
                payoutStatus === 'error' ? 'bg-theme-error-bg text-theme-error' :
                  'bg-theme-bg-muted text-theme-text-secondary'
                }`}>
                {payoutStatus === 'success' ? <CheckCircle className="h-4 w-4" /> :
                  payoutStatus === 'error' ? <AlertCircle className="h-4 w-4" /> : null}
                {message}
              </div>
            )}

            <button
              type="submit"
              disabled={payoutStatus === 'processing' || !revenue}
              className="btn btn-primary w-full"
            >
              {payoutStatus === 'processing' ? (
                <>
                  <LoadingSpinner size="sm" />
                  Processing Transfers...
                </>
              ) : (
                'Calculate & Distribute'
              )}
            </button>
          </form>
        </div>
      </div>
    </Layout>
  );
}
